import { FC, useMemo, useState } from "react"
import InfoSvg from '@/shared/assets/images/Info.svg'
import BonusSvg from '@/shared/assets/images/BonusMenuIcon.svg'
import { BuyTicketButton } from "../buy-ticket-button"
import HeartSvg from '@/shared/assets/images/Heart.svg'
import { ISearchFlight } from "@/shared/api/aviabooking"
import { useAppSelector } from "@/shared/lib/redux"
import { AirportInfo, selectAirlinesMap, selectAirportsMap, TransporterIcon } from "@/entities/aviabooking-reference"
import FlightSvg from '@/shared/assets/images/AirplaneTakeoff.svg'
import { morph } from "@/shared/lib/word-morph"
import { FlightSegmentsBar, getSearchFlightTotalPrice } from "@/entities/flight"
import { getFreeSeatsCount } from "../../lib/flight-availability"
import type { useSavedFlights } from "../../lib/use-saved-flights"
import { snapshotFromFlight } from "@/shared/api/aviabooking"
import { Modal } from "@/shared/ui/modal"
import { FlightItinerary } from "./flight-itinerary"

interface TicketProps {
    flight: ISearchFlight
    saved: ReturnType<typeof useSavedFlights>
}

export const Ticket: FC<TicketProps> = ({ flight, saved }) => {
    const [detailsOpen, setDetailsOpen] = useState(false)
    const isSaved = saved.isSaved(flight.id)
    const snapshot = useMemo(() => snapshotFromFlight(flight), [flight])
    const airports = useAppSelector(selectAirportsMap)
    const airlines = useAppSelector(selectAirlinesMap)

    const totalPrice = useMemo(() => getSearchFlightTotalPrice(flight), [flight])

    const freeSeatsCount = useMemo(() => getFreeSeatsCount(flight), [flight])

    const flightAirlines = useMemo(() => {
        return flight.segments.map(segment => airlines[segment.marketing_airline]).filter(Boolean)
    }, [flight, airlines])

    const depAirport = useMemo(() => {
        return airports[flight.dep_airport_code]
    }, [airports, flight])

    const arrAirport = useMemo(() => {
        return airports[flight.arr_airport_code]
    }, [airports, flight])

    return (
        <div className="w-full bg-white flex rounded-3xl shadow-sm">
            <div className="border-r p-6 shrink-0 flex flex-col gap-4">
                <div className="flex w-full gap-1 items-center justify-center">
                    <div className="font-bold text-xl">
                        {totalPrice}
                    </div>
                    <div className="fill-white">
                        <BonusSvg width={26} height={26} />
                    </div>
                </div>
                <div className="border w-full items-center flex justify-between rounded-2xl py-3 px-3 min-w-[200px]">
                    <div>
                        {freeSeatsCount === null
                            ? 'Наличие мест уточняется'
                            : `${freeSeatsCount} ${morph(freeSeatsCount, ['свободное место', 'свободных места', 'свободных мест'])}`}
                    </div>
                    <button type="button" onClick={() => setDetailsOpen(true)} aria-label="Подробности о наличии мест" className="text-slate-600">
                        <InfoSvg width={16} height={16} />
                    </button>
                </div>
                <div>
                    <BuyTicketButton flight={flight} disabled={freeSeatsCount === 0} />
                </div>
            </div>
            <div className="p-6 w-full overflow-hidden flex flex-col">
                <div className="w-full flex items-center justify-between">
                    <div className="flex shrink-0 gap-2">
                        {flightAirlines.map(airline =>
                            <TransporterIcon
                                key={airline.code}
                                name={airline?.name}
                                icon={airline ? airline.logo : FlightSvg as any}
                            />
                        )}
                    </div>
                    <div className="flex shrink-0 gap-4">
                        <button type="button" aria-label={isSaved ? "Удалить из сохранённых" : "Сохранить рейс"} aria-pressed={isSaved}
                            disabled={saved.pending !== null || (!(flight.id in saved.matches) && !isSaved)} onClick={() => void saved.toggle(flight)}
                            className={`cursor-pointer hover:text-color-primary transition-all disabled:opacity-50 ${isSaved ? "text-red-600" : "text-slate-400"}`}>
                            <HeartSvg width={30} height={30} />
                        </button>
                        <button type="button" aria-label="Подробности рейса" onClick={() => setDetailsOpen(true)} className="text-slate-500 cursor-pointer hover:text-color-primary transition-all">
                            <InfoSvg width={30} height={30} />
                        </button>
                    </div>
                </div>
                <div className="flex items-center gap-4 h-full justify-between w-full">
                    <div className="shrink-0">
                        <AirportInfo
                            pin
                            timestamp={flight.segments[0].dep_date_time}
                            name={depAirport?.code}
                            city={depAirport?.city}
                        />
                    </div>
                    <FlightSegmentsBar segments={flight.segments} />
                    <div className="shrink-0">
                        <AirportInfo
                            end
                            timestamp={flight.segments.at(-1).arr_date_time}
                            name={arrAirport?.code}
                            city={arrAirport?.city}
                        />
                    </div>
                </div>
            </div>
            <Modal isOpen={detailsOpen} title="Подробности рейса" onRequestClose={() => setDetailsOpen(false)}>
                <div className="flex flex-col gap-3">
                    {snapshot ? <FlightItinerary snapshot={snapshot} /> : <p>Данные о маршруте или цене недоступны.</p>}
                    <p>Свободных мест: {freeSeatsCount === null ? "уточняется" : freeSeatsCount}</p>
                    {flight.prices?.[0]?.fares?.map((fare, index) =>
                        <p key={index}>Тариф {fare.type}: {fare.quantity} × {fare.total_fare} {fare.currency} (сборы: {fare.taxes} {fare.currency})</p>)}
                    <p className="text-sm text-slate-500">Цена и доступность уточняются при оформлении.</p>
                </div>
            </Modal>
        </div>
    )
}